import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Stack from 'react-bootstrap/Stack'

import { formatConfidence } from '../../utilities/recommendationCards'

import './recommendationCard.scss'

const CATEGORY_LABELS = {
  promotion: 'Promotion',
  pricing: 'Pricing',
  marketing: 'Marketing',
  operations: 'Operations',
}

const CATEGORY_ACCENTS = {
  promotion: 'category-promotion',
  pricing: 'category-pricing',
  marketing: 'category-marketing',
  operations: 'category-operations',
}

const PRIORITY_VARIANT = {
  high: 'danger',
  medium: 'warning',
  low: 'secondary',
}

const STATUS_LABELS = {
  pending: 'Pending',
  approved: 'Approved',
  dismissed: 'Dismissed',
  executed: 'Executed',
}

function priorityClass(priority) {
  const p = String(priority || 'medium').toLowerCase()
  if (p === 'high') return 'priority-high'
  if (p === 'low') return 'priority-low'
  return 'priority-medium'
}

/**
 * @param {{
 *   card: import('../../utilities/recommendationCards/types').RecommendationCard;
 *   onPrimaryAction?: (card: import('../../utilities/recommendationCards/types').RecommendationCard) => void;
 *   onApprove?: (card: import('../../utilities/recommendationCards/types').RecommendationCard) => void;
 *   onDismiss?: (card: import('../../utilities/recommendationCards/types').RecommendationCard) => void;
 *   isActionLoading?: boolean;
 * }} props
 */
export default function RecommendationCard({
  card,
  onPrimaryAction,
  onApprove,
  onDismiss,
  isActionLoading = false,
}) {
  const category = card.category || 'promotion'
  const priority = card.priority || 'medium'
  const status = card.status || 'pending'
  const isResolved =
    status === 'approved' || status === 'dismissed' || status === 'executed'

  return (
    <Card
      body
      className={`card--xs card-with-border recommendation-card ${CATEGORY_ACCENTS[category] || ''} ${priorityClass(priority)} ${isResolved ? 'is-resolved' : ''}`}
    >
      <Stack gap={3} className="h-100">
        <Stack
          direction="horizontal"
          className="flex-wrap gap-2 align-items-start recommendation-card__header"
        >
          <Badge
            bg="light"
            text="dark"
            className="recommendation-card__category"
          >
            {CATEGORY_LABELS[category] || category}
          </Badge>
          <Badge
            bg={PRIORITY_VARIANT[priority] || 'secondary'}
            className="text-capitalize"
          >
            {priority}
          </Badge>
          <Badge
            bg={
              status === 'dismissed'
                ? 'secondary'
                : status === 'approved'
                  ? 'success'
                  : 'light'
            }
            text={status === 'pending' ? 'dark' : undefined}
            className="ms-auto"
          >
            {STATUS_LABELS[status] || status}
          </Badge>
        </Stack>

        <Stack gap={2} className="flex-grow-1">
          <div>
            <h3 className="h6 mb-1 recommendation-card__title">{card.title}</h3>
            <p className="mb-0 recommendation-card__subtitle">
              {card.subtitle}
            </p>
          </div>

          <Stack
            direction="horizontal"
            gap={3}
            className="recommendation-card__metrics flex-wrap"
          >
            <div className="recommendation-card__metric">
              <span className="recommendation-card__metric-label">
                {card.primaryMetric?.label}
              </span>
              <span className="recommendation-card__metric-value">
                {card.primaryMetric?.value}
              </span>
            </div>
            {card.secondaryMetric && (
              <div className="recommendation-card__metric">
                <span className="recommendation-card__metric-label">
                  {card.secondaryMetric.label}
                </span>
                <span className="recommendation-card__metric-value">
                  {card.secondaryMetric.value}
                </span>
              </div>
            )}
          </Stack>

          <div className="recommendation-card__confidence">
            <Stack
              direction="horizontal"
              className="justify-content-between mb-1"
            >
              <span className="recommendation-card__metric-label">
                Confidence
              </span>
              <span className="recommendation-card__confidence-value">
                {formatConfidence(card.confidenceScore)}
              </span>
            </Stack>
            <div className="recommendation-card__confidence-track">
              <div
                className="recommendation-card__confidence-fill"
                style={{
                  width: `${Math.min(100, Math.max(0, card.confidenceScore || 0))}%`,
                }}
              />
            </div>
          </div>

          {card.suggestedActions?.length > 0 && (
            <ul className="recommendation-card__actions list-unstyled mb-0">
              {card.suggestedActions.map((action) => (
                <li key={`${action.type}-${action.label}`}>{action.label}</li>
              ))}
            </ul>
          )}
        </Stack>

        <Stack
          direction="horizontal"
          gap={2}
          className="recommendation-card__footer flex-wrap"
        >
          <Button
            variant="primary"
            size="sm"
            disabled={isActionLoading || isResolved}
            onClick={() => onPrimaryAction?.(card)}
          >
            {card.ctaLabel}
          </Button>
          {!isResolved && (
            <>
              <Button
                variant="outline-success"
                size="sm"
                disabled={isActionLoading}
                onClick={() => onApprove?.(card)}
              >
                Approve
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={isActionLoading}
                onClick={() => onDismiss?.(card)}
              >
                Dismiss
              </Button>
            </>
          )}
        </Stack>
      </Stack>
    </Card>
  )
}
